// Core packages
import { formatDate } from "@angular/common";
import { Component, Inject } from "@angular/core";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";

// Custom packages
import { HelperService } from "src/app/shared/services/helper.service";
import {
  PrivacyDocsService,
  STUDIO_AUTHORIZED_PERSON_APPOINTMENT_TEMPLATE,
  STUDIO_CONFIDENTIALITY_AGREEMENT_TEMPLATE,
  STUDIO_EMPLOYEE_PRIVACY_TEMPLATE,
  STUDIO_PEC_DELIVERY_DELEGATION_TEMPLATE,
  STUDIO_SUBPROCESSOR_APPOINTMENT_TEMPLATE,
} from "src/app/shared/services/privacy-docs.service";

interface IStudioPrivacyTemplateRow {
  labelKey: string;
  templateType: string;
  loading: boolean;
}

interface IStudioPrivacyTemplatesDialogData {
  companyId: string;
}

/**
 * Script start
 */
@Component({
  selector: "app-studio-privacy-templates-dialog",
  templateUrl: "./studio-privacy-templates-dialog.component.html",
  styleUrls: ["./studio-privacy-templates-dialog.component.scss"]
})
export class StudioPrivacyTemplatesDialogComponent {
  rows: IStudioPrivacyTemplateRow[] = [
    {
      labelKey: "companies.detail.privacyDocsCard.studioSection.templatesDialog.docs.employeePrivacyNotice",
      templateType: STUDIO_EMPLOYEE_PRIVACY_TEMPLATE,
      loading: false,
    },
    {
      labelKey: "companies.detail.privacyDocsCard.studioSection.templatesDialog.docs.subProcessorAppointment",
      templateType: STUDIO_SUBPROCESSOR_APPOINTMENT_TEMPLATE,
      loading: false,
    },
    {
      labelKey: "companies.detail.privacyDocsCard.studioSection.templatesDialog.docs.authorizedPersonAppointment",
      templateType: STUDIO_AUTHORIZED_PERSON_APPOINTMENT_TEMPLATE,
      loading: false,
    },
    {
      labelKey: "companies.detail.privacyDocsCard.studioSection.templatesDialog.docs.confidentialityAgreement",
      templateType: STUDIO_CONFIDENTIALITY_AGREEMENT_TEMPLATE,
      loading: false,
    },
    {
      labelKey: "companies.detail.privacyDocsCard.studioSection.templatesDialog.docs.pecDeliveryDelegation",
      templateType: STUDIO_PEC_DELIVERY_DELEGATION_TEMPLATE,
      loading: false,
    },
  ];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<StudioPrivacyTemplatesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IStudioPrivacyTemplatesDialogData,
    private privacyDocsService: PrivacyDocsService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
  ) { }

  /**
   * Handle a row's "Scarica" button click: request the back-end to fill
   * the given template with this company's registry data and download
   * the resulting editable Word file
   *
   * @since 1.0.0
   */
  onDownload(row: IStudioPrivacyTemplateRow): void {
    if (row.loading) {
      return;
    }
    if (!this.data || !this.data.companyId) {
      this.toastrService.error(
        this.translateService.instant("companies.detail.privacyDocsCard.studioSection.templatesDialog.missingCompany")
      );
      return;
    }

    row.loading = true;
    this.privacyDocsService
      .generateStudioDocument(this.data.companyId, row.templateType)
      .subscribe(
        (fileBlob: Blob) => {
          row.loading = false;
          const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
          const fileNamePrefix = this.translateService.instant(row.labelKey);
          FileSaver.saveAs(fileBlob, `${fileNamePrefix}-${date}.docx`);
          this.toastrService.success(
            this.translateService.instant("companies.detail.privacyDocsCard.studioSection.templatesDialog.success")
          );
        },
        (err) => {
          row.loading = false;
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle "Chiudi": close the dialog
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
